import React, { useState, useEffect } from "react";
import { Typography, Box, Button, CircularProgress } from "@mui/material";
import Layout from "../../components/Layout";
import { useAppContext } from "../../context/AppContext";
import { SkinScore } from "../../types";
import { getConcernTranslation } from "../../services/concern-mappings";
import ApiService from "../../services/api";
import { COMMON_MUI_STYLES } from "../../styles";

// Utility function to retry network errors once
const retryOnce = async <T,>(
  fn: () => Promise<T>,
  delay: number = 1500
): Promise<T> => {
  try {
    return await fn();
  } catch (error: any) {
    // Only retry network errors (they're often transient)
    const isNetworkError = 
      error?.isNetworkError || 
      error?.errorType === 'NETWORK_ERROR' ||
      (error?.request && !error?.response);

    if (isNetworkError) {
      await new Promise(resolve => setTimeout(resolve, delay));
      // Retry once
      return await fn();
    }
    // For other errors, throw immediately (no retry)
    throw error;
  }
};

const ScoresPage: React.FC = () => {
  const {
    skinScores: _skinScores,
    setCurrentView,
    userId,
    role,
    setRedirectAfterAuth,
    sessionId,
    getTranslation,
    token,
    setSnackbar
  } = useAppContext();
  const [skinHealth, setSkinHealth] = useState<number>(0);
  const [selectedConcern, setSelectedConcern] = useState<string>("");
  const [concernImageSrc, setConcernImageSrc] = useState<string>("");
  const [skinScores, setSkinScores] = useState<SkinScore[]>([]);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [isSkipDisabled, setIsSkipDisabled] = useState(false);
  const riskLevelName: { [key: number]: string } = {
    3: getTranslation("Txt751"),
    2: getTranslation("Txt752"),
    1: getTranslation("Txt753"),
  };

  // Initialize API service with session ID
  const apiService = new ApiService(sessionId, token);

  useEffect(() => {
    if (_skinScores) {
      setSkinHealth(
        _skinScores.skinScores.find(
          (score: SkinScore) => score.concern === "skin_health"
        )?.score || 0
      );

      const sortedSkinScores = _skinScores.skinScores
        .filter((s) => s.concern !== "skin_health")
        .sort((s1, s2) => {
          return s1.score - s2.score;
        });
      setSkinScores(sortedSkinScores);
      concernSelected(sortedSkinScores[0].concern);
    }
  }, [_skinScores]);

  const concernSelected = (concern: string) => {
    setSelectedConcern(concern);
    setConcernImageSrc(_skinScores ? _skinScores?.annotations[concern] : "");
  };

  const handleSendReport = async () => {
    // For distributors (role 1), just navigate to feedback without any API calls
    if (role === 1) {
      setCurrentView("feedback");
      return;
    }

    // For customers (role 2), call the APIs as before
    setIsSkipDisabled(true);

    try {
      setIsSendingReport(true);

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        retryOnce(() => apiService.sendReport()),
        retryOnce(() => apiService.updateSessionReport(false)),
      ]);

      // Check if any failed
      const failures = results.filter(r => r.status === 'rejected');
      
      if (failures.length > 0) {
        // Log failures but don't throw (we still navigate)
        failures.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(`Report API ${index === 0 ? 'sendReport' : 'updateSessionReport'} failed:`, result.reason);
          }
        });

        // Show error message
        const firstError = failures[0].reason;
        const isNetworkError = 
          firstError?.isNetworkError || 
          firstError?.errorType === 'NETWORK_ERROR';

        setSnackbar({
          open: true,
          severity: "error",
          message: isNetworkError
            ?  "Network Error: Unable to send report. Please check your internet connection."
            : "Failed to send report. Please try again later.",
        });
      }

      // Always navigate to feedback (even if report failed)
      setCurrentView("feedback");

    } catch (error: any) {
      // This catch is for unexpected errors
      console.error("Unexpected error in handleSendReport:", error);
      
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to send report. Please try again later.",
      });

      setCurrentView("feedback");
    } finally {
      setIsSendingReport(false);
    }
  };

  return (
    <Layout title="Skin Scores" showBackButton={false}>
      {skinScores && skinScores.length && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px" }}>
          <Box className="skin-scores-container">
            <Box
              sx={{
                width: "100%",
                overflow: "hidden",
                gridRow: { xs: 2, sm: 1 },
              }}
            >
              <Typography
                sx={{
                  mb: 3,
                  fontWeight: "400",
                  fontSize: "1.25rem",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {getTranslation("Txt200").replace("-", " ")}{skinHealth}%
              </Typography>
              <Box sx={{ position: "relative" }}>
                <Box
                  className="skin-scores"
                  sx={{
                    transition: "all 0.3s ease",
                  }}
                >
                  {skinScores &&
                    skinScores.map((score: SkinScore, index: number) => (
                      <Box
                        className="score-selector"
                        key={index}
                        sx={{
                          filter:
                            !userId && role === 2
                              ? index === 0
                                ? "none"
                                : "blur(4px)"
                              : "none",
                          pointerEvents:
                            !userId && role === 2
                              ? index === 0
                                ? "auto"
                                : "none"
                              : "auto",
                          opacity:
                            !userId && role === 2 ? (index === 0 ? 1 : 0.7) : 1,
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Box
                          className={`r${score.riskLevel} score center ${
                            score.concern === selectedConcern ? "selected" : ""
                          }`}
                          onClick={() => {
                            if (
                              (!userId && role === 1) ||
                              (userId && role === 2) ||
                              index === 0
                            ) {
                              concernSelected(score.concern);
                            }
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "16px", md: "28px" },
                              fontWeight: 700,
                            }}
                          >
                            {score.score}%
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: "8px", md: "12px" },
                              textAlign: "center",
                              width: "auto",
                            }}
                          >
                            {riskLevelName[score.riskLevel]}
                          </Typography>
                        </Box>
                        <Typography className="concern-name">
                        {getConcernTranslation(getTranslation, score.concern)}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
              <Typography
                sx={{
                  mt: 2,
                }}
                className="disclaimer"
              >
                {!userId && role === 2
                  ? getTranslation("Txt215")
                  : getTranslation("Txt240")}
              </Typography>
              <Box className="center">
                {!userId && role === 2 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      ...COMMON_MUI_STYLES.primaryCtaButton1,
                      mt: 5,
                      mb: 2,
                    }}
                    onClick={() => {
                      setRedirectAfterAuth && setRedirectAfterAuth("scores");
                      setCurrentView("signup");
                    }}
                  >
                    {getTranslation("Txt216")}
                  </Button>
                ) : (
                  <>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 2,
                    width: '100%',
                    mt: 3,
                    mb: 2
                  }}>
                    {role === 2 && (
                    <Typography
                      sx={{
                        fontSize: { xs: "12px", md: "14px" },
                        fontWeight: 500,
                        color: 'text.secondary',
                        textAlign: 'center',
                        maxWidth: '300px',
                        lineHeight: 1.4
                      }}
                    >
                      {getTranslation("Txt827")}
                    </Typography>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{ 
                        ...COMMON_MUI_STYLES.primaryCtaButton1,
                        width: { xs: '80%', sm: '50%' },
                        mt: 2,
                        mb: 1,
                      }}
                      onClick={handleSendReport}
                      disabled={isSendingReport}
                    >
                      {isSendingReport ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} sx={{ color: "#FFF" }} />
                        </Box>
                      ) : getTranslation("Txt688")}
                    </Button>
                    {role === 2 && (
                    <Typography
                      sx={{
                        fontSize: { xs: "11px", md: "13px" },
                        fontWeight: 500,
                        textDecoration: 'underline',
                        cursor: isSkipDisabled ? 'not-allowed' : 'pointer',
                        color: 'text.secondary',
                        opacity: isSkipDisabled ? 0.5 : 0.8,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: isSkipDisabled ? 'none' : 'auto',
                        '&:hover': {
                          opacity: 1,
                          color: 'primary.main'
                        }
                      }}
                      onClick={async () => {
                        if (isSkipDisabled) {
                          return;
                        }
                        setIsSkipDisabled(true);
                        try {
                          await apiService.updateSessionReport(true);
                          setCurrentView("feedback");
                        } catch (error) {
                          console.error('Failed to skip report update:', error);
                          setIsSkipDisabled(false);
                        }
                      }}
                    >
                      {getTranslation("Txt846")}
                    </Typography>
                    )}
                  </Box>
                  </>
                )}
              </Box>
            </Box>

            <Box className="concern-image">
              <img src={concernImageSrc} crossOrigin="anonymous" />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "2px",
                  // width: "95%",
                }}
              >
                {/* Item 1 */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: "3px", md: "10px" },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "12px", md: "25px" },
                      height: { xs: "12px", md: "25px" },
                      borderRadius: "50%",
                      backgroundColor: "#FF6961",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "10px", md: "12px" },
                      fontWeight: 500,
                    }}
                  >
                    {getTranslation("Txt201")}
                  </Typography>
                </Box>

                {/* Item 2 */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: "3px", md: "10px" },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "12px", md: "25px" },
                      height: { xs: "12px", md: "25px" },
                      borderRadius: "50%",
                      backgroundColor: "#FFB347",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "10px", md: "12px" },
                      fontWeight: 500,
                    }}
                  >
                    {getTranslation("Txt202")}
                  </Typography>
                </Box>

                {/* Item 3 */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: "3px", md: "10px" },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "12px", md: "25px" },
                      height: { xs: "12px", md: "25px" },
                      borderRadius: "50%",
                      backgroundColor: "#77DD77",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "10px", md: "12px" },
                      fontWeight: 500,
                    }}
                  >
                    {getTranslation("Txt203")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ 
            width: { xs: "95%", sm: "90%", md: "80%" }, 
            backgroundColor: "#F5F5F5", 
            padding: { xs: "8px", sm: "10px", md: "12px" }, 
            borderRadius: "10px", 
            margin: "0 auto" 
          }}>
            <Typography sx={{ fontSize: { xs: "10px", md: "14px" }, fontWeight: 400, textAlign: "center" }}>
            {getTranslation("Txt847")}
            </Typography>
          </Box>
        </Box>
      )}
    </Layout>
  );
};

export default ScoresPage;
